import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { ImageSection } from 'app/components/ImageSection';
import { RootState } from 'app/store/types';
import { DocumentUploadButton } from 'app/components/DocumentUploadButton';
import download from 'downloadjs';
import { getFormValues } from 'redux-form';
import { FieldNames } from '../../models/fields/names';
import moment from 'moment';
import { FormType } from '@lenderful/domain';
import { generateFinancialStatementPdf } from 'app/api/financial-statement';
import { useNotifications } from 'app/providers/notifications/NotificationProvider';
import { NotificationLevel } from 'app/providers/notifications/Notification';
import { ButtonDelay } from 'app/components/ButtonDelay';

type MappedProps = ReturnType<typeof mapStateToProps>;

const FinancialStatementConfirmationComponent = (props: MappedProps) => {
  const { confirmationImage, values } = props;

  const { t } = useTranslation();
  const { displayNotification } = useNotifications();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    setIsDownloading(true);
    try {
      const loanApplicationId = values[FieldNames.universalApplicationId];
      const response = await generateFinancialStatementPdf(loanApplicationId);
      const filename = `${values[FieldNames.lastName]}-${values[FieldNames.firstName]}-${moment().millisecond()}-Financial-Statement`;
      
      download(response.statement, filename, 'application/pdf');
    } catch {
      displayNotification('The financial statement PDF was unable to be generated.', NotificationLevel.ERROR);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Grid container={false}>
      <Grid item classes={{ item: 'noStepper' }}>
        <ImageSection image={confirmationImage}>
          <Typography variant='h6' classes={{ h6: 'titleMargin section' }}>
            {t('confirmation.title', { defaultValue: 'Thank you!' })}
          </Typography>
          <Typography variant='body2' paragraph={true}>
            {t('confirmation.paragraph1', {
              defaultValue:
                'Please download your Personal Financial Statement, sign it, and upload the final copy.',
            })}
          </Typography>
          <Grid container justifyContent='center'>
            <Grid item xs={12}>
              <ButtonDelay
                loading={ isDownloading }
                buttonProps={{
                  classes  : { root: 'longButton' },
                  color    : 'primary',
                  disabled : isDownloading,
                  variant  : 'contained',
                  onClick  : handleClick,
                }}
                pendingText={ 'Downloading' }
              >
                {t('financialStatement.button.download', { defaultValue: 'Download Statement' })}
              </ButtonDelay>
              <DocumentUploadButton />
            </Grid>
          </Grid>
        </ImageSection>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: RootState, ownProps: any) => ({
  confirmationImage : state.config.confirmationImage,
  values            : getFormValues(FormType.FINANCIAL_STATEMENT)(state),
  ...ownProps,
});

export const FinancialStatementConfirmation = connect(mapStateToProps)(FinancialStatementConfirmationComponent);
